import i18n from '../../i18n.js';

// Calculates the number of days since the passed date.
export const daysSince = (pastDateString) => {
  // 1. Create Date objects for the past date and today
  const pastDate = new Date(pastDateString);
  const today = new Date();

  // Ensure dates are valid
  if (isNaN(pastDate.getTime()) || isNaN(today.getTime())) {
    return "Invalid date provided";
  }

  // To avoid issues with Daylight Saving Time (DST) and timezones,
  // it is best to set both dates to midnight UTC before comparison.
  const utcToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const utcPastDate = Date.UTC(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate());

  // 2. Calculate the difference in milliseconds
  const millisecondsSince = utcToday - utcPastDate;

  // 3. Define the number of milliseconds in a day (24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
  const millisecondsPerDay = 1000 * 60 * 60 * 24; // 86400000

  // 4. Convert the milliseconds difference to days and round down to the nearest whole day
  const daysPassed = Math.floor(millisecondsSince / millisecondsPerDay);

  // 5. The minus one support "Day Zero" 
  return daysPassed - 1;
}


export const ageInMonths = (birthDateString) => {
  if (!birthDateString) return null;
  return Math.floor(daysSince(birthDateString) / 30);
};

export const DateToAgeCategory = (date, t) => {
  const months = ageInMonths(date);
  if (months === null) return '—';
  const m = t ? t('age_months') : 'months';
  if (months <= 12)  return `0-12 ${m}`;
  if (months <= 24)  return `13-24 ${m}`;
  if (months <= 36)  return `25-36 ${m}`;
  return `37+ ${m}`;
};

export const API_BASE = process.env.REACT_APP_API_URL || '';

// Returns Authorization + optional X-Ranch-ID headers for authenticated requests.
export const authHeaders = (extraHeaders = {}) => {
  const token = localStorage.getItem('zapmanejo_token');
  const ranchID = localStorage.getItem('zapmanejo_ranch_id');
  const headers = {
    "Authorization": `Bearer ${token}`,
    ...extraHeaders,
  };
  if (ranchID) headers["X-Ranch-ID"] = ranchID;
  return headers;
};

export const Fetch = (url, method, body, success, error) => {
  // Ensure URL is root-relative so it works regardless of the current React Router path
  const normalizedUrl = url.startsWith('/') || url.startsWith('http') ? url : `/${url}`;
  const headers = authHeaders({ "Content-Type": "application/json" });

  fetch(`${API_BASE}${normalizedUrl}`, {
    method: method,
    headers,
    body: body,
  })
  .then(response => {
    if (response.status === 401) {
      localStorage.removeItem('zapmanejo_token');
      localStorage.removeItem('zapmanejo_user');
      window.location.reload();
      return;
    }

    // Check for updated user token (e.g., after profile changes)
    const newUser = response.headers.get("x-new-user");
    if (newUser && typeof newUser === "string" && newUser !== "") {
      localStorage.setItem('zapmanejo_user', newUser);
    }

    if (!response.ok) {
      // Read the body before throwing — most handlers in this app send the
      // real reason as plain text (http.Error), and without this the error
      // callback below gets nothing to show the user beyond "it failed."
      return response.text().then(text => {
        let message = text || `HTTP error! status: ${response.status}`;
        // Newer handlers send {"error": "<i18n_key>"} instead of raw text
        // specifically so the failure reason can be localized here, once,
        // instead of every caller showing whatever language the Go backend
        // happened to hardcode. defaultValue falls back to the key itself
        // if no translation exists yet, and older handlers' plain-text
        // bodies (not valid JSON) fall through to the text as before.
        try {
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed.error === 'string' && parsed.error) {
            message = i18n.t(parsed.error, { defaultValue: parsed.error });
          }
        } catch (e) {
          // Not JSON — plain-text error body, use it as-is.
        }
        throw new Error(message);
      });
    }

    const contentLength = response.headers.get("content-length");
    if (response.status === 204 || (contentLength && parseInt(contentLength) === 0)) {
      if (typeof success === "function") {
        success();
      }
      return;
    }

    return response.json()
  })
  .then(json => {
    if (json !== undefined && typeof success === "function") {
      success(json);
    }
  })
  .catch(err => {
    if (typeof error === "function") {
      error(err.message);
    }
  });
}
